/**
 * Shared money-amount parsing/formatting helpers, built on `currency.js`
 * (the project's money library — never hand-roll number parsing).
 *
 * `parseAmount` is the single source of truth for turning a human-typed /
 * imported amount string into a number. It is used by BOTH the file/projection
 * importers (packages/tasks) AND the manual entry forms (apps/web) so the two
 * paths can never diverge (DCP-224/225). It was originally defined in
 * packages/tasks/src/lib/import-utils.ts and moved here unchanged; that module
 * now re-exports it for backward compatibility.
 */
import currency from 'currency.js'

/**
 * Upper bound on a raw amount string before we reject it outright. 50 chars
 * comfortably fits the longest realistic input: sign + thousands-grouped integer
 * + decimal part + currency symbol (e.g. "-1,234,567,890,123.45 USD" is ~27 chars).
 */
export const MAX_AMOUNT_STRING_LENGTH = 50

/**
 * Parse a raw amount string (typed or imported) into a numeric value.
 *
 * US number format ONLY (confirmed with BA, 2026-06-18) — parsing does NOT
 * rely on the browser/OS locale:
 * - Comma thousands + dot decimal: `1,234.56` → 1234.56
 * - Plain thousands grouping: `500,500.00` → 500500
 * - Plain integer / decimal: `1234`, `1234.56`
 * - Accounting-style negatives: `(500.00)` → -500
 * - Unicode MINUS SIGN (U+2212) and the `$` symbol / surrounding whitespace
 *
 * Returns `null` for anything outside the strict accepted shapes — including
 * European notation (`1.234,56`), comma-decimals (`1234,56`), foreign currency
 * symbols, alpha, scientific notation, double minus and dangling separators —
 * so callers surface a validation error instead of silently storing a wrong
 * number.
 *
 * @param raw - Raw amount string from a CSV/XLSX cell or a form input
 * @returns Parsed numeric value, or null if the input is not a valid number
 */
export function parseAmount(raw: string): number | null {
  let cleaned = raw.trim()

  if (cleaned.length > MAX_AMOUNT_STRING_LENGTH) return null

  // Normalize Unicode MINUS SIGN (U+2212) to ASCII hyphen-minus.
  // Excel and other spreadsheet exports frequently emit U+2212 instead of
  // U+002D for negative numbers; without this, the whitelist regex below
  // would reject otherwise valid negative amounts.
  cleaned = cleaned.replace(/−/g, '-')

  // Strip the US dollar symbol and any surrounding/internal whitespace (e.g.
  // "$ 1,234.56", "( 500.00 )"). Done BEFORE the accounting-negative check so
  // parenthesised negatives with inner spaces are still recognised. Foreign
  // currency symbols are intentionally NOT stripped so non-USD cells fail the
  // whitelist below (US-only product).
  cleaned = cleaned.replace(/\$/g, '').replace(/\s/g, '')

  // Convert accounting negatives: (500.00) → -500.00 (also "( 500.00 )", since
  // the inner whitespace was stripped above).
  let isNeg = false
  if (cleaned.startsWith('(') && cleaned.endsWith(')')) {
    isNeg = true
    cleaned = cleaned.slice(1, -1)
  }

  if (!cleaned) return null

  // Strict US-format whitelist: optional leading minus, comma as the (3-digit)
  // thousands separator, dot as the decimal separator. Runs after
  // currency/whitespace/paren stripping so "$1,234.56" → "1,234.56" passes.
  // This guard is the single source of truth for accepted shapes; currency.js
  // only parses an already-validated string.
  //
  // Two accepted shapes inside the alternation:
  //   1) Plain integer with an optional dot-decimal tail: `1234`, `1234.56`.
  //   2) Comma-grouped integer (`1,234`, `1,234,567`) with an optional
  //      dot-decimal tail.
  // Rejects European notation (`1.234,56`), comma-decimals (`1234,56`, `9,5`),
  // mis-grouped numbers (`12,3456`, `1.2.3.4`), alpha, scientific notation
  // (`1e5`), double minus (`--42`), dangling separators (`.50`, `50.`) and
  // non-ASCII digits.
  if (!/^-?(?:\d+|\d{1,3}(?:,\d{3})+)(?:\.\d+)?$/.test(cleaned)) return null

  // US format is fixed: comma = thousands separator, dot = decimal.
  // precision: 10 preserves arbitrary decimal places (e.g. "1234.5678" → 1234.5678).
  const opts: currency.Options = {
    symbol: '',
    errorOnInvalid: true,
    precision: 10,
    separator: ',',
    decimal: '.',
  }

  try {
    const result = currency(cleaned, opts)
    const value = result.value
    // Number.isFinite (not the global isFinite) — the global coerces strings,
    // which masks a future regression where `currency()` returns a non-number
    // and aligns with the rest of the import code (validate{Row,ProjectionRow}
    // already use Number.isFinite).
    if (!Number.isFinite(value)) return null
    return isNeg ? -value : value
  } catch {
    return null
  }
}

/**
 * Format a numeric amount into a locale-stable string for an editable text
 * input: ASCII dot decimal separator, NO thousands grouping, 2 decimal places.
 *
 * Used to pre-fill amount fields in Edit forms. A bare `<input type="number">`
 * renders a JS number using the OS locale, which on a comma-decimal locale
 * (e.g. macOS set to a European region) shows "129404,6" instead of
 * "129404.60" (DCP-225). Producing the string ourselves with currency.js keeps
 * it dot-decimal everywhere. No grouping separator keeps the value trivially
 * re-editable (and re-parseable by `parseAmount`, which accepts commas anyway).
 *
 * @param amount - The numeric amount to render
 * @returns e.g. 129404.6 → "129404.60", -500 → "-500.00"
 */
export function formatAmountForInput(amount: number): string {
  return currency(amount, { symbol: '', separator: '', precision: 2 }).format()
}

/**
 * Round a numeric amount to exactly 2 decimal places (cents).
 *
 * Every amount in this product is currency backed by a `DECIMAL/NUMERIC(15,2)`
 * database column, so sub-cent precision has no meaning — it can only create
 * drift between the in-memory model, what the user was shown, and what
 * actually gets persisted. This helper is the canonicalization step for
 * amounts entering form state (see `AmountInput.handleChange`).
 *
 * Uses currency.js (not `Math.round(value * 100) / 100`) because plain
 * floating-point rounding of money is exactly the class of bug currency.js
 * exists to avoid (e.g. `Math.round(1.005 * 100) / 100` famously yields `1`
 * instead of `1.01` on IEEE-754 doubles).
 *
 * It rounds the MAGNITUDE and re-applies the sign, because currency.js on its
 * own is not sign-symmetric at the half-cent: `currency(500.005).value` is
 * `500.01` but `currency(-500.005).value` is `-500`. Both literals are the
 * same double magnitude (`500.00499999999999545...`); currency.js lifts it to
 * the exact midpoint with an epsilon nudge and then hands it to `Math.round`,
 * whose tie-break goes toward +Infinity — so it rounds away from zero for a
 * positive amount and toward zero for a negative one. Postgres, the authority
 * this helper exists to agree with, has no such asymmetry:
 * `(-500.005)::numeric(15,2)` is `-500.01`, exactly mirroring
 * `(500.005)::numeric(15,2)` = `500.01` (verified against the project's own
 * Postgres). Since signed amounts are real in this product (projections and
 * manual transactions store one signed column), rounding the magnitude is what
 * keeps a debit and a credit of the same size one cent apart from disagreeing.
 *
 * `-0` is deliberately collapsed to `0`: a value whose magnitude rounds to
 * zero has no sign worth carrying, and `-0` would otherwise leak into form
 * state and `JSON` payloads for no benefit.
 *
 * Deliberately NOT applied inside `parseAmount`: that function also feeds the
 * CSV/XLSX importers (packages/tasks), where preserving higher precision from
 * imported data is a separate decision with a different blast radius.
 * Rounding is applied only at the UI component boundary (`AmountInput`).
 *
 * @param value - The amount to round; `NaN`/`±Infinity` are returned unchanged
 *   so callers can keep relying on non-finite values as their "unparseable" /
 *   "no value" signal.
 * @returns The value rounded to 2 decimal places, or the input unchanged if
 *   it is not finite.
 */
export function roundToCents(value: number): number {
  if (!Number.isFinite(value)) return value
  const magnitude = currency(Math.abs(value), { precision: 2 }).value
  return value < 0 && magnitude !== 0 ? -magnitude : magnitude
}

/**
 * Sums money amounts in integer cents (a double `reduce` drifts: `0.1 + 0.2`).
 * A non-finite entry is a caller bug and makes the result NaN, not skipped.
 */
export function sumAmounts(amounts: readonly number[]): number {
  return amounts.reduce((total, amount) => total.add(amount), currency(0)).value
}
